import { useState, useEffect } from 'react';
import { PartyPopper, Plus, Edit2, Trash2, Eye, EyeOff, X, Send, Bell, Users, MapPin, Crown, AlertTriangle, CheckCircle, Power, PowerOff, Sparkles, XCircle } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { formatDistanceToNow } from '../utils/date-utils';
import { AnnouncementModal } from './AnnouncementModal'; // ✅ NEW: Import AnnouncementModal for preview
import { devError } from '../utils/logger';

interface AdminAnnouncementsProps {
  accessToken: string;
}

interface Announcement {
  id: string;
  title: string;
  message: string;
  buttonText: string;
  buttonDestination: string;
  showToAll: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function AdminAnnouncements({ accessToken }: AdminAnnouncementsProps) {
  const adminModalZ = useAdminModalZIndex();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deletingAnnouncementId, setDeletingAnnouncementId] = useState<string | null>(null);
  const [showPushModal, setShowPushModal] = useState<string | null>(null);
  const [showInAppModal, setShowInAppModal] = useState<string | null>(null);
  const [previewModalAnnouncement, setPreviewModalAnnouncement] = useState<Announcement | null>(null); // ✅ NEW: Preview modal state
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [buttonDestination, setButtonDestination] = useState('');
  const [showToAll, setShowToAll] = useState(false);

  // Push/In-app filters
  const [filters, setFilters] = useState({
    fylke: '',
    user_type: '', // ✅ FIXED: Changed userType to user_type to match backend
    subscription: ''
  });
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  const fylker = [
    'Agder', 'Akershus', 'Buskerud', 'Finnmark', 'Innlandet',
    'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland', 'Telemark',
    'Troms', 'Trøndelag', 'Vestfold', 'Vestland', 'Østfold'
  ];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/announcements`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      } else {
        toast.error('Kunne ikke hente announcements');
      }
    } catch (error) {
      devError('Error fetching announcements:', error);
      toast.error('Feil ved henting av announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!title.trim() || !message.trim() || !buttonText.trim()) {
      toast.error('Tittel, melding og knapp-tekst er påkrevd');
      return;
    }

    try {
      const url = editingAnnouncement
        ? `${API_BASE}/admin/announcements/${editingAnnouncement.id}`
        : `${API_BASE}/admin/announcements`;

      const response = await fetch(url, {
        method: editingAnnouncement ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          title,
          message,
          button_text: buttonText,
          button_destination: buttonDestination,
          show_to_all: showToAll,
        }),
      });

      if (response.ok) {
        toast.success(editingAnnouncement ? 'Announcement oppdatert!' : 'Announcement opprettet!');
        resetForm();
        fetchAnnouncements();
      } else {
        toast.error('Kunne ikke lagre announcement');
      }
    } catch (error) {
      devError('Error saving announcement:', error);
      toast.error('Feil ved lagring');
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingAnnouncementId(id);
    try {
      const response = await fetch(
        `${API_BASE}/admin/announcements/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Announcement slettet!');
        fetchAnnouncements();
      } else {
        toast.error('Kunne ikke slette announcement');
      }
    } catch (error) {
      devError('Error deleting announcement:', error);
      toast.error('Feil ved sletting');
    } finally {
      setDeletingAnnouncementId(null);
      setShowDeleteConfirm(null);
    }
  };

  const handleToggleActive = async (id: string, currentlyActive: boolean) => {
    try {
      const endpoint = currentlyActive ? 'deactivate' : 'activate';
      const response = await fetch(
        `${API_BASE}/admin/announcements/${id}/${endpoint}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success(currentlyActive ? 'Announcement deaktivert!' : 'Announcement aktivert!');
        fetchAnnouncements();
      } else {
        toast.error('Kunne ikke endre status');
      }
    } catch (error) {
      devError('Error toggling active:', error);
      toast.error('Feil ved endring av status');
    }
  };

  const handlePreviewUsers = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/preview-users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ filters }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewCount(data.count);
      } else {
        toast.error('Kunne ikke hente forhåndsvisning');
      }
    } catch (error) {
      devError('Error previewing users:', error);
      toast.error('Feil ved forhåndsvisning');
    }
  };

  const handleSendPush = async (announcementId: string) => {
    setSending(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/announcements/${announcementId}/send-push`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ filters }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Push sendt til ${data.pushSent} av ${data.count} brukere!`);
        setShowPushModal(null);
        setFilters({ fylke: '', user_type: '', subscription: '' }); // ✅ FIXED: Changed userType to user_type
        setPreviewCount(null);
      } else {
        toast.error('Kunne ikke sende push');
      }
    } catch (error) {
      devError('Error sending push:', error);
      toast.error('Feil ved sending av push');
    } finally {
      setSending(false);
    }
  };

  const handleSendInApp = async (announcementId: string) => {
    setSending(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/announcements/${announcementId}/send-inapp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ filters }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`In-app varsel sendt til ${data.count} brukere!`);
        setShowInAppModal(null);
        setFilters({ fylke: '', user_type: '', subscription: '' });
        setPreviewCount(null);
      } else {
        toast.error('Kunne ikke sende in-app varsel');
      }
    } catch (error) {
      devError('Error sending in-app:', error);
      toast.error('Feil ved sending av in-app varsel');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setButtonText('');
    setButtonDestination('');
    setShowToAll(false);
    setEditingAnnouncement(null);
    setShowCreateModal(false);
  };

  const openEditModal = (announcement: Announcement) => {
    setTitle(announcement.title);
    setMessage(announcement.message);
    setButtonText(announcement.buttonText);
    setButtonDestination(announcement.buttonDestination);
    setShowToAll(announcement.showToAll);
    setEditingAnnouncement(announcement);
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl text-gray-800">Announcement Modals</h2>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Ny Announcement
          </button>
        </div>
        <p className="text-gray-600 text-sm">
          Lag announcements som vises som modaler til brukere via push/in-app varsler eller automatisk ved innlogging.
        </p>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Loader size="lg" className="text-purple-600" />
            <p className="text-gray-600 mt-4">Laster kunngjøringer...</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Ingen announcements ennå</p>
            <p className="text-gray-500 text-sm mt-2">Opprett din første announcement for å komme i gang</p>
          </div>
        ) : (
          announcements.map((announcement) => (
            <div
              key={announcement.id}
              className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                announcement.isActive
                  ? 'border-green-200 bg-green-50/50'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">{announcement.title}</h3>
                    {announcement.isActive && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Aktiv
                      </span>
                    )}
                    {announcement.showToAll && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Vises til alle
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-3 whitespace-pre-line">{announcement.message}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Knapp: "{announcement.buttonText}"</span>
                    {announcement.buttonDestination && (
                      <span>→ {announcement.buttonDestination}</span>
                    )}
                    <span>•</span>
                    <span>{formatDistanceToNow(announcement.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(announcement.id, announcement.isActive)}
                    className={`p-2 rounded-lg transition-colors ${
                      announcement.isActive
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={announcement.isActive ? 'Deaktiver' : 'Aktiver'}
                  >
                    {announcement.isActive ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openEditModal(announcement)}
                    className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Rediger"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(announcement.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                    title="Slett"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Send actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {/* ✅ NEW: Preview button */}
                <button
                  onClick={() => setPreviewModalAnnouncement(announcement)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 rounded-xl hover:from-blue-200 hover:to-cyan-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Forhåndsvis Modal
                </button>
                <button
                  onClick={() => setShowPushModal(announcement.id)}
                  className="flex-1 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send Push
                </button>
                <button
                  onClick={() => setShowInAppModal(announcement.id)}
                  className="flex-1 px-4 py-2 bg-pink-100 text-pink-700 rounded-xl hover:bg-pink-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Bell className="w-4 h-4" />
                  Send In-App
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            onClick={resetForm}
            style={{ zIndex: 'var(--z-important)' }}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-important)' }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editingAnnouncement ? 'Rediger Announcement' : 'Ny Announcement'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Tittel *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="F.eks. Ny funksjon!"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Melding *</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Skriv meldingen som brukerne vil se..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Knapp-tekst *</label>
                  <input
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder="F.eks. Les mer"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={50}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Knapp-destinasjon</label>
                  <select
                    value={buttonDestination}
                    onChange={(e) => setButtonDestination(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Ingen (bare lukk modal)</option>
                    <option value="home">Home</option>
                    <option value="messages">Messages</option>
                    <option value="profile">Profile</option>
                    <option value="subscription">Subscription</option>
                    <option value="gigs">Gigs</option>
                    <option value="my-gigs">My Gigs</option>
                    <option value="my-interests">My Interests</option>
                    <option value="gig-interests">Gig Interests</option>
                    <option value="favorites">Favorites</option>
                    <option value="saved-searches">Saved Searches</option>
                    <option value="settings">Settings</option>
                    <option value="faq">FAQ</option>
                    <option value="about">About</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <input
                    type="checkbox"
                    id="showToAll"
                    checked={showToAll}
                    onChange={(e) => setShowToAll(e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <label htmlFor="showToAll" className="text-gray-700 text-sm">
                    <span className="font-medium">Vis til alle ved innlogging</span>
                    <p className="text-gray-600 text-xs mt-1">
                      Hvis avkrysset: Modal vises kun til de som mottar push/in-app varsel
                    </p>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleCreateOrUpdate}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                  >
                    {editingAnnouncement ? 'Oppdater' : 'Opprett'}
                  </button>
                  <button
                    onClick={resetForm}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Push Modal */}
      {showPushModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 'var(--z-important)' }}
            onClick={() => setShowPushModal(null)}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-important)' }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Send Push-varsel</h3>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fylke</label>
                    <select
                      value={filters.fylke}
                      onChange={(e) => setFilters({ ...filters, fylke: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      {fylker.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Brukertype</label>
                    <select
                      value={filters.user_type} 
                      onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      <option value="talent">Talent</option>
                      <option value="employer">Arbeidsgiver</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Abonnement</label>
                    <select
                      value={filters.subscription}
                      onChange={(e) => setFilters({ ...filters, subscription: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      <option value="free">Gratis</option>
                      <option value="plus">Talentfunn Pluss</option>
                      <option value="employer">Arbeidsgiver Basis</option>
                      <option value="employer_plus">Arbeidsgiver Pluss</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={handlePreviewUsers}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Forhåndsvis antall
                </button>

                {previewCount !== null && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-blue-900 font-medium">{previewCount} brukere matcher filteret for push</p>
                    <p className="text-blue-700 text-xs mt-1">Faktisk levering avhenger av aktive push-innstillinger på enhetene.</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => handleSendPush(showPushModal)}
                    disabled={sending}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {sending ? 'Sender...' : 'Send Push'}
                  </button>
                  <button
                    onClick={() => setShowPushModal(null)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* In-App Modal */}
      {showInAppModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 'var(--z-important)' }}
            onClick={() => setShowInAppModal(null)}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-important)' }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Send In-App Varsel</h3>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fylke</label>
                    <select
                      value={filters.fylke}
                      onChange={(e) => setFilters({ ...filters, fylke: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      {fylker.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Brukertype</label>
                    <select
                      value={filters.user_type} 
                      onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      <option value="talent">Talent</option>
                      <option value="employer">Arbeidsgiver</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Abonnement</label>
                    <select
                      value={filters.subscription}
                      onChange={(e) => setFilters({ ...filters, subscription: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Alle</option>
                      <option value="free">Gratis</option>
                      <option value="plus">Talentfunn Pluss</option>
                      <option value="employer">Arbeidsgiver Basis</option>
                      <option value="employer_plus">Arbeidsgiver Pluss</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={handlePreviewUsers}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Forhåndsvis antall
                </button>

                {previewCount !== null && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-blue-900 font-medium">{previewCount} brukere vil motta in-app varsel</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => handleSendInApp(showInAppModal)}
                    disabled={sending}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {sending ? 'Sender...' : 'Send In-App'}
                  </button>
                  <button
                    onClick={() => setShowInAppModal(null)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}
        title="Slett announcement?"
        message="Er du sikker på at du vil slette denne announcement? Denne handlingen kan ikke angres."
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={!!deletingAnnouncementId}
        loadingText="Sletter..."
        portalZIndex={adminModalZ}
      />

      {/* ✅ NEW: Preview Announcement Modal */}
      <AnnouncementModal
        isOpen={!!previewModalAnnouncement}
        announcement={previewModalAnnouncement}
        onClose={() => setPreviewModalAnnouncement(null)}
        onActionClick={() => {
          toast.info('Dette er kun en forhåndsvisning - ingen navigering');
        }}
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
